import { useState, useRef, useEffect } from "react";
import "./Tailwind.css";
import { Building2, Copy, Check, ExternalLink } from "lucide-react"; // vector icons

function cleanResearchMarkdown(text) {
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim();
}

function parseCompanyResearch(text) {
  const sections = [];
  let currentSection = { title: "Overview", content: [] };
  const trimmedText = String(text || "").trim();
  const hasMatchingOuterQuotes =
    trimmedText.length > 1 &&
    (trimmedText[0] === '"' || trimmedText[0] === "'") &&
    trimmedText.at(-1) === trimmedText[0];
  const normalizedText = hasMatchingOuterQuotes
    ? trimmedText.slice(1, -1)
    : trimmedText;

  normalizedText
    .split("\n")
    .forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) return;

      if (/^\|.*\|$/.test(line)) {
        const cells = line
          .slice(1, -1)
          .split("|")
          .map(cleanResearchMarkdown);
        const isSeparator = cells.every((cell) => /^:?-{3,}:?$/.test(cell));
        const isHeader =
          cells[0]?.toLowerCase() === "item" &&
          cells[1]?.toLowerCase() === "details";

        if (!isSeparator && !isHeader && cells[0] && cells[1]) {
          currentSection.content.push({
            type: "detail",
            label: cells[0],
            text: cells.slice(1).join(" | "),
          });
        }
        return;
      }

      const markdownHeading = line.match(/^#{1,6}\s+(.+)$/);
      const boldHeading = line.match(/^\*\*([^*]+)\*\*:?$/);
      const plainHeading =
        line.length <= 70 && line.endsWith(":") && !/^https?:/i.test(line)
          ? line.slice(0, -1)
          : "";
      const heading = markdownHeading?.[1] || boldHeading?.[1] || plainHeading;

      if (heading) {
        if (currentSection.content.length) sections.push(currentSection);
        currentSection = {
          title: cleanResearchMarkdown(heading),
          content: [],
        };
        return;
      }

      const listItem = line.match(/^(?:[-*•]|\d+[.)])\s+(.+)$/);
      currentSection.content.push({
        type: listItem ? "list" : "paragraph",
        text: cleanResearchMarkdown(listItem?.[1] || line),
      });
    });

  if (currentSection.content.length) sections.push(currentSection);
  return sections;
}

function CompanyResearch({ research }) {
  if (!research) {
    return (
      <div className="flex min-h-60 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 text-center">
        <Building2 className="mb-3 h-7 w-7 text-slate-400" aria-hidden="true" />
        <p className="max-w-sm text-sm leading-6 text-slate-500">
          Company products, priorities, and recent developments will appear
          here when you generate a cover letter.
        </p>
      </div>
    );
  }

  const sections = parseCompanyResearch(research);

  return (
    <div className="max-h-96 space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:p-4">
      {sections.map((section, sectionIndex) => (
        <section
          key={`${section.title}-${sectionIndex}`}
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        >
          <h3 className="mb-2 text-sm font-semibold text-slate-800">
            {section.title}
          </h3>
          <div className="space-y-2 text-sm leading-6 text-slate-600">
            {section.content.map((item, itemIndex) =>
              item.type === "detail" ? (
                <dl
                  key={itemIndex}
                  className="grid gap-1 border-b border-slate-100 pb-2 last:border-0 last:pb-0 sm:grid-cols-[8rem_1fr] sm:gap-4"
                >
                  <dt className="font-medium text-slate-700">{item.label}</dt>
                  <dd className="whitespace-pre-line text-slate-600">
                    {item.text}
                  </dd>
                </dl>
              ) : item.type === "list" ? (
                <div
                  key={itemIndex}
                  className="flex items-start gap-2 border-l-2 border-slate-200 pl-3"
                >
                  <span>{item.text}</span>
                </div>
              ) : (
                <p key={itemIndex}>{item.text}</p>
              ),
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

function App() {
  const inputRef = useRef(null);
  const [isResume, setIsResume] = useState(false);
  const [resumeATS, setResumeATS] = useState();
  const [structeredResume, setStructeredResume] = useState();
  const [previewUrl, setPreviewUrl] = useState(null);
  //const baseUrl = "https://fwdmailer.com";
  const baseUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:5002"
      : "https://fwdmailer.com";
  const [resumeFile, setResumeFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [companyInfo, setCompanyInfo] = useState("");
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const [resumeATSLoading, setResumeATSLoading] = useState(false);
  const [resumeATSError, setResumeATSError] = useState("");
  const [generatedLetter, setGeneratedLetter] = useState("");
  const [loading, setLoading] = useState(false);
  const [createGoogleDocLoading, setCreateGoogleDocLoading] = useState(false);
  const [createGoogleDocResumeLoading, setCreateGoogleDocResumeLoading] =
    useState(false);
  const [processingStage, setProcessingStage] = useState("idle");
  const [letterLength, setLetterLength] = useState("medium");
  const [copied, setCopied] = useState(false);
  const canSubmit = Boolean(resumeFile && jobDescription.trim());

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setResumeFile(file);

      // Create preview if PDF
      if (file.type === "application/pdf") {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const loadDummyResume = async () => {
    const response = await fetch("/junior_dev_resume.pdf");
    const blob = await response.blob();
    const file = new File([blob], "junior_dev_resume.pdf", { type: blob.type });

    // ✅ put file into input element
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    inputRef.current.files = dataTransfer.files;

    // Trigger the same onChange handler
    handleFileUpload({ target: { files: dataTransfer.files } });
    const perplexityJobDescription =
      // "AI Software Engineer - Agent Platform New York City; Palo Alto; San Francisco Perplexity is an AI-powered answer engine founded in December 2022 and growing rapidly as one of the worlds leading AI platforms. Perplexity has raised over $1B in venture investment from some of the worlds most visionary and successful leaders, including Elad Gil, Daniel Gross, Jeff Bezos, Accel, IVP, NEA, NVIDIA, Samsung, and many more. Our objective is to build accurate, trustworthy AI that powers decision-making for people and assistive AI wherever decisions are being made. Throughout human history, change and innovation have always been driven by curious people. Today, curious people use Perplexity to answer more than 780 million queries every month - a number thats growing rapidly for one simple reason: everyone can be curious. Perplexity is seeking an experienced AI Agent Software Engineer to help revolutionize the way people search and interact online. In this role, you will build scalable and elastic agent platform to power wide span of agent workloads from search to research to browser. Responsibilities Architect and implement next-gen agent platform that powers all agentic products at Perplexity with high efficiency, reliability and scalability. Stay up-to-date on new features released from external LLM providers and in house researchers and provide unified interfaces to interact with different sizes of models. Improve the speed and quality of AI agent codebase and engineering processes to ensure we consistently deliver an amazing product experience. Hold a high quality bar to build generalized agentic & AI evaluation system to power all product online and offline evaluations. Collaborate across disciplines, including researchers, engineers, product managers, and designers, to fill gaps and ship products. Qualifications Past experience building agent applications with tool calling, MCP and context engineering. Understands how to make tradeoffs in using different models to achieve the goal with most cost efficient and reliable system design. Self-motivated with a willingness to take ownership of platform components. 4+ years of industry experience. [Preferred] Past experience with open source LLM post training and evaluation experiences. The cash compensation range for this role is $200,000 - $300,000. Final offer amounts are determined by multiple factors, including experience and expertise, and may vary from the amounts listed.";
      "Full Stack Software Engineer New York City; San Francisco Perplexity is an AI-powered answer engine founded in December 2022 and growing rapidly as one of the world’s leading AI platforms. Perplexity has raised over $1B in venture investment from some of the world’s most visionary and successful leaders, Our objective is to build accurate, trustworthy AI that powers decision-making for people and assistive AI wherever decisions are being made. Throughout human history, change and innovation have always been driven by curious people. Today, curious people use Perplexity to answer more than 780 million queries every month–a number that’s growing rapidly for one simple reason: everyone can be curious. \n\nPerplexity is seeking an experienced Full Stack Engineer to help revolutionize the way people search and interact online. In this role, you'll translate cutting-edge AI advances into products that are both useful and engaging. Tech Stack: TypeScript | Python | Docker | AWS | Vercel\n\nTeams Currently Hiring\nEnterprise\nThe Enterprise Team at Perplexity designs and builds core platform features like rich file attachments, secure file and app connectors (using cutting-edge Model Context Protocol), and Spaces for streamlined collaboration—capabilities widely used by both enterprise and regular users. The team owns uniquely challenging domains at the intersection of AI, automation, and knowledge management, directly shaping how millions of people work with private data, files, and productivity apps across Perplexity. Their small, expert group operates with high autonomy, developing systems that set industry standards and deliver massive, visible product impact.\n\nGrowth\n\nThe Growth team shapes how millions of people experience Perplexity. We rapidly experiment and launch new ideas across all user-facing surfaces at Perplexity including onboarding, verticals like Students, and premium Max Tier. We build AI-powered features such as personalized prompts, auto-generated study guides, and contextual upsell nudges, driving user activation, retention, and revenue across all platforms. We own Perplexity’s growth platform, including experimentation framework, feature flagging, and notifications, to mobilize impactful feature development across the company. Engineers have outsized impact, building both the product and the platform that power our company’s growth." +
      "\n\nBilling\nThe Billing team powers how users subscribe to and pay for Perplexity, building seamless subscription experiences across all tiers. We partner with business and finance teams to define new pricing and subscription models, while also driving growth through partner integrations that expand distribution and brand presence. We develop and maintain secure, scalable systems—including agentic payments that autonomously interact with payment platforms—ensuring reliable financial transactions for millions of users worldwide. By shaping both user experience and revenue infrastructure, the Billing team sits at the core of Perplexity’s growth and sustainability.\n\nConsumer Verticals\nWith AI-powered exploration and browsing, personalization and memory, live data visualization, comparison, and checkout, Perplexity is reimagining the consumer experience across verticals such as travel, shopping, health, real estate, finance, sports, live events and ticketing, and more. Each of these verticals represents an opportunity for Perplexity to disrupt massive markets dominated by slower-moving incumbents. Agentic actions powered by Comet unlock novel consumer experiences such as automated shopping and travel purchases, restaurant reservations, complete travel itineraries planned and booked from scratch, automated investing, and more.\n\nResponsibilities\nBuilding new 0-1 products at high scale\nWorking closely with Product, Design, and Data to ship experiments and learn\nLaunching new features, experiments, campaigns, and partnerships in a fast-moving environment\nBuilding core growth infrastructure such as notification platforms, ad attribution, and more\nAnalyzing performance metrics and user feedback to identify opportunities for improvement and optimization\nBuilding delightful and data-proven user journeys\n\nQualifications\nStrong programming skills with the ability to work across the full stack\nSelf-motivated with a willingness to take ownership of tasks\nGood quantitative understanding of data and experimentation\nExperience making data-driven decisions and measuring impact of those decisions (experimentation, feature flags, adhoc analysis)\nA passion for shipping quality products\n4+ years of industry experience\nThe compensation range for this role is $180,000 - $300,000";
    setJobDescription(perplexityJobDescription);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // reset after 2s
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleGenerateLetter = async () => {
    if (!resumeFile) {
      alert("Please upload a resume.");
      return;
    }
    let processingStateTimeout1, processingStateTimeout2;

    setLoading(true);
    setGeneratedLetter("");
    await researchTheCompany();

    if (!companyInfoLoading) {
      setProcessingStage("Processing resume...");
      // setTimeout(
      //   () => setProcessingStage("Processing resume..."),
      //   3000
      // );
      processingStateTimeout1 = setTimeout(
        () => setProcessingStage("Deep thinking & reasoning..."),
        2000
      );
      processingStateTimeout2 = setTimeout(
        () => setProcessingStage("Generating cover letter..."),
        4000
      );
    }
    try {
      const formData = new FormData();
      formData.append("resume", resumeFile);
      formData.append("jobDescription", jobDescription);
      formData.append("companyInfo", companyInfo);
      formData.append("letterLength", letterLength);
      if (isResume) {
        formData.append("resType", "resume-ats");
      }
      const response = await fetch(`${baseUrl}/deepreason`, {
        method: "POST",
        body: formData,
      });

      if (!response.body) {
        setLoading(false);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      //let resultText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        //resultText += chunk;
        // console.log("chunk: ", chunk);
        setGeneratedLetter((prev) => prev + chunk);
      }

      setLoading(false);
      clearTimeout(processingStateTimeout1);
      clearTimeout(processingStateTimeout2);
    } catch (err) {
      console.error("Error fetching cover letter:", err);
      setLoading(false);
      console.log(err.message);
    }
  };

  const handleGenerateResume = async () => {
    if (!canSubmit) {
      setResumeATSError("Upload a resume and paste a job description first.");
      return;
    }
    let processingStateTimeout1, processingStateTimeout2;

    setResumeATSLoading(true);
    setResumeATSError("");
    // setGeneratedLetter("");
    //await researchTheCompany();

    // if (!companyInfoLoading) {
    //   setProcessingStage("Processing resume...");
    //   // setTimeout(
    //   //   () => setProcessingStage("Processing resume..."),
    //   //   3000
    //   // );
    //   processingStateTimeout1 = setTimeout(
    //     () => setProcessingStage("Deep thinking & reasoning..."),
    //     2000
    //   );
    //   processingStateTimeout2 = setTimeout(
    //     () => setProcessingStage("Generating cover letter..."),
    //     4000
    //   );
    // }
    try {
      const formData = new FormData();
      formData.append("resume", resumeFile);
      formData.append("jobDescription", jobDescription);
      formData.append("companyInfo", companyInfo);
      formData.append("letterLength", letterLength);
      formData.append("resType", "resume-ats");

      const response = await fetch(`${baseUrl}/deepreason`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("The scan could not be completed. Please try again.");
      }
      //const data = await response.text();
      const data = await response.json();
      const normalizedResumeATS = {
        ...data.resumeATS,
        overall_score: data.resumeATS?.overall_score ?? 0,
        pass: data.resumeATS?.pass ?? false,
        matched_skills: data.resumeATS?.matched_skills ?? [],
        missing_skills: data.resumeATS?.missing_skills ?? [],
        recommendations: data.resumeATS?.recommendations ?? [],
      };

      console.log(data);
      setStructeredResume(data.structeredResume);
      setResumeATS(normalizedResumeATS);
      //let resultText = "";

      setResumeATSLoading(false);
      // clearTimeout(processingStateTimeout1);
      // clearTimeout(processingStateTimeout2);
    } catch (err) {
      console.error("Error fetching cover letter:", err);
      setResumeATSError(
        err.message || "The scan could not be completed. Please try again."
      );
      setResumeATSLoading(false);
    }
  };
  // const handleDownloadPDF = async () => {
  //   if (!generatedLetter) {
  //     alert("Generate a cover letter first.");
  //     return;
  //   }

  //   try {
  //     const response = await fetch((`${baseUrl}/generate-pdf`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ coverLetter: generatedLetter }),
  //     });

  //     if (!response.ok) {
  //       throw new Error("Failed to generate PDF.");
  //     }

  //     // ✅ Convert response to binary Blob
  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);

  //     // ✅ Create a download link
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = "cover_letter.pdf";
  //     document.body.appendChild(a);
  //     a.click();
  //     document.body.removeChild(a);
  //   } catch (err) {
  //     console.error("Error downloading PDF:", err);
  //     alert("Error generating PDF.");
  //   }
  // };

  const researchTheCompany = async () => {
    if (!jobDescription) {
      alert("Job description is missing");
      return;
    }
    setCompanyInfo("");
    setCompanyInfoLoading(true);

    try {
      //const response = await fetch("/create-google-doc", {
      const response = await fetch(`${baseUrl}/research-the-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobDescription: jobDescription }),
      });

      if (!response.ok) {
        throw new Error("Failed to research the company.");
      }

      //const { data } = await response.json();
      const data = await response.text(); // always safe

      // console.log("Company info: ", data);
      setCompanyInfo(data);
      setCompanyInfoLoading(false);
    } catch (err) {
      console.error("Error researching the company", err);
      setLoading(false);
      setCompanyInfoLoading(false);
      alert("Error researching the company");
    }
  };
  const handleCreateGoogleDoc = async () => {
    if (!generatedLetter) {
      alert("Generate a cover letter first.");
      return;
    }
    setCreateGoogleDocLoading(true);

    try {
      //const response = await fetch("/create-google-doc", {
      const response = await fetch(`${baseUrl}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coverLetter: generatedLetter }),
      });

      if (!response.ok) {
        throw new Error("Failed to create Google Doc.");
      }

      const { data } = await response.json();
      //console.log(data);
      setCreateGoogleDocLoading(false);
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl; // ✅ Redirects to Stripe Checkout
      }
    } catch (err) {
      console.error("Error creating Google Doc:", err);
      setCreateGoogleDocLoading(false);
      alert("Error creating Google Doc.");
    }
  };
  const handleCreateGoogleDocResume = async () => {
    if (!resumeATS) {
      alert("Scan resume first.");
      return;
    }
    setCreateGoogleDocResumeLoading(true);

    try {
      //const response = await fetch("/create-google-doc", {
      const response = await fetch(
        `${baseUrl}/create-resume-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            structeredResume: structeredResume,
            jobDescription: jobDescription,
            resumeATS: resumeATS,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create Google Doc Resume.");
      }

      const { data } = await response.json();
      //console.log(data);
      setCreateGoogleDocResumeLoading(false);
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl; // ✅ Redirects to Stripe Checkout
      }
    } catch (err) {
      console.error("Error creating Google Doc Resume:", err);
      setCreateGoogleDocResumeLoading(false);
      alert("Error creating Google Doc Resume.");
    }
  };

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  return (
    <div className="px-6 pb-6 max-w-8xl mx-auto flex flex-col items-center bg-gray-100 min-h-screen">
      <div className="mt-4 text-sm font-bold tracking-widest text-gray-500 uppercase">
        DEEP-LETTER
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mt-4 text-center text-gray-900">
        Tailor your application to every job
      </h1>
      <p className="max-w-2xl mb-5 text-center text-gray-600">
        Check how well your resume matches a job, or create a tailored cover
        letter in minutes.
      </p>
      <div
        className="tabs tabs-boxed bg-white border border-gray-200 p-1 mb-5"
        role="tablist"
        aria-label="Choose a DEEP-LETTER tool"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isResume}
          className={`tab px-6 ${!isResume ? "tab-active" : ""}`}
          onClick={() => setIsResume(false)}
        >
          Cover Letter
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isResume}
          className={`tab px-6 ${isResume ? "tab-active" : ""}`}
          onClick={() => setIsResume(true)}
        >
          Resume ATS Scan
        </button>
      </div>

      {/* <div className="card bg-white shadow-lg rounded-lg p-6 w-full mb-6">
        <h2 className="text-xl font-semibold mb-3 text-gray-700">
          Paste Job Description
        </h2>
        <textarea
          placeholder="Paste job description here..."
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          className="textarea textarea-bordered w-full h-80 rounded-lg p-3"
        />
      </div> */}

      {/* <div className="grid grid-cols-2 gap-6 w-full"> */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
        {/* Job Description Field */}
        <div className="card bg-white shadow-lg rounded-lg p-6">
          <div className="card bg-white shadow-lg rounded-lg p-3.5 w-full mb-6">
            <h2 className="text-lg font-semibold mb-3 text-gray-700">
              Upload Your Resume
            </h2>
            <div className="flex flex-col items-start gap-4 border rounded-lg p-5">
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(event) => handleFileUpload(event)}
                // className="file-input file-input-md file-input-primary"
                className="file-input file-input-bordered file-input-neutral file-input-md w-full"
              />
              <button
                onClick={loadDummyResume}
                className="btn btn-sm px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
              >
                Try with sample Resume &amp; Job Description
              </button>
            </div>

            {resumeFile && (
              <div className="mt-4 text-sm text-gray-700">
                <p>
                  <span className="font-semibold">File:</span> {resumeFile.name}
                </p>
                <p>
                  <span className="font-semibold">Size:</span>{" "}
                  {(resumeFile.size / 1024).toFixed(2)} KB
                </p>
                <p>
                  <span className="font-semibold">Type:</span> {resumeFile.type}
                </p>

                {/* ✅ PDF Preview */}
                {previewUrl && (
                  <div className="mt-3 border rounded-lg overflow-hidden">
                    <iframe
                      src={previewUrl}
                      title="PDF Preview"
                      className="w-full h-96"
                    ></iframe>
                  </div>
                )}
              </div>
            )}
          </div>

          <h2 className="text-xl font-semibold mb-3 text-gray-700">
            Job Description
          </h2>
          <textarea
            value={jobDescription}
            placeholder="Paste your job description"
            onChange={(e) => setJobDescription(e.target.value)}
            className="textarea textarea-bordered w-full h-[25rem] rounded-lg p-3"
          />

          {!isResume && (
            <>
              <h2 className="text-xl font-semibold mt-4 text-gray-700">
                Cover letter length
              </h2>
              <div className="flex flex-row justify-between bg-white shadow-lg rounded-lg p-3.5 px-10 w-full mb-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="letterLength"
                    value="short"
                    checked={letterLength === "short"}
                    className="radio"
                    // className="h-6 w-6 checked:bg-green-500 text-green-500 p-3 my-4"
                    onChange={(e) => setLetterLength(e.target.value)}
                  />
                  <span>Short</span>
                  {/* <span>Short (≤200 words)</span> */}
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="letterLength"
                    value="medium"
                    checked={letterLength === "medium"}
                    className="radio"
                    onChange={(e) => setLetterLength(e.target.value)}
                  />
                  <span>Medium</span>
                  {/* <span>Medium (200–350 words)</span> */}
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="letterLength"
                    value="long"
                    checked={letterLength === "long"}
                    className="radio"
                    onChange={(e) => setLetterLength(e.target.value)}
                  />
                  <span>Long</span>
                  {/* <span>Long (350–500 words)</span> */}
                </label>
              </div>
            </>
          )}
          {isResume ? (
            <>
              {!canSubmit && (
                <p className="mt-3 text-sm text-gray-500">
                  Upload a resume and paste the job description to begin.
                </p>
              )}
              <button
                onClick={handleGenerateResume}
                disabled={!canSubmit || resumeATSLoading}
                className="btn btn-primary w-full my-2 py-3 text-base-100 font-semibold rounded-lg shadow-md hover:bg-[#2b2a2a]"
              >
                {resumeATSLoading ? "Scanning resume..." : "Check Resume Match"}
              </button>
            </>
          ) : (
            <>
              {!canSubmit && (
                <p className="mt-3 text-sm text-gray-500">
                  Upload a resume and paste the job description to begin.
                </p>
              )}
              <button
                onClick={handleGenerateLetter}
                disabled={!canSubmit || loading}
                className="btn btn-primary w-full my-2 py-3 text-base-100 font-semibold rounded-lg shadow-md hover:bg-[#2b2a2a]"
              >
                {loading ? "Generating..." : "Generate Cover Letter"}
              </button>
            </>
          )}
        </div>
        {/* Generated Cover Letter Field */}
        <div className="card bg-white shadow-lg rounded-lg p-6">
          {!isResume ? (
            <div className="mb-4 text-center">
              <h2 className="text-xl font-semibold text-gray-700">
                Company Research
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Current context used to make your letter more specific.
              </p>
            </div>
          ) : (
            <h2 className="text-xl text-center font-semibold mb-3 text-gray-700">
              Resume ATS
            </h2>
          )}
          {companyInfoLoading ? (
            <div className="flex flex-row space-y-2 text-gray-600 text-sm">
              <span className="animate-spin inline w-4 h-4 border-2 mx-2 border-gray-500 border-t-transparent rounded-full"></span>
              {"   "}
              Searching about the company...
              {/* {processingStage} */}
            </div>
          ) : (
            ""
          )}
          {resumeATSLoading ? (
            <div className="flex items-center mb-3 text-gray-600 text-sm">
              <span className="animate-spin inline w-4 h-4 border-2 mx-2 border-gray-500 border-t-transparent rounded-full"></span>
              Comparing your resume with the job description...
            </div>
          ) : (
            ""
          )}
          {isResume && resumeATSError && (
            <div className="alert alert-error mb-3 text-sm" role="alert">
              <span>{resumeATSError}</span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleGenerateResume}
                disabled={!canSubmit || resumeATSLoading}
              >
                Retry
              </button>
            </div>
          )}
          {isResume ? (
            <div
              className={
                resumeATS
                  ? "space-y-5 border border-gray rounded-lg p-4"
                  : "flex min-h-64 items-center justify-center border border-gray rounded-lg p-6 bg-gray-100"
              }
            >
              {resumeATS ? (
                <>
                  <div className="flex items-center">
                    <div className="text-4xl font-bold mr-4">
                      {resumeATS.overall_score} %
                    </div>
                    <div>
                      <div className="font-semibold">ATS Score</div>
                      <div className="text-sm text-gray-500">
                        {resumeATS.pass ? "Pass" : "Fail"}
                        {/* • Confidence:{" "}
                      {(resumeATS.confidence_overall * 100).toFixed(0)}% */}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold">Matched skills</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {resumeATS.matched_skills.length > 0 ? (
                        resumeATS.matched_skills.map((s) => (
                          <span key={s.skill} className="badge badge-outline">
                            {s.skill} ({Math.round(s.confidence * 100)}%)
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">
                          No exact skill matches found.
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold">Missing skills</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {resumeATS.missing_skills.length > 0 ? (
                        resumeATS.missing_skills.map((s) => (
                          <span key={s} className="badge badge-error text-white">
                            {s}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">
                          No missing skills detected.
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold">Top recommendations</h3>
                    <ol className="list-decimal ml-6 mt-2">
                      {resumeATS.recommendations.length > 0 ? (
                        resumeATS.recommendations.slice(0, 5).map((r) => (
                          <li key={r.id} className="mb-2">
                            <div className="font-semibold">
                              {r.title}{" "}
                              <span className="text-sm text-gray-500">
                                ({r.impact})
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {r.description}
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="list-none -ml-6 text-sm text-gray-500">
                          No recommendations available for this scan.
                        </li>
                      )}
                    </ol>
                  </div>
                </>
              ) : (
                <p className="max-w-sm text-center text-sm text-gray-500">
                  Your match score, missing skills, and recommendations will
                  appear here after the scan.
                </p>
              )}
            </div>
          ) : (
            <CompanyResearch research={companyInfo} />
          )}

          {/* {!isResume && (
            <p className="whitespace-pre-line h-32 overflow-y-auto text-gray-600 text-sm min-h-[15rem] p-3 leading-relaxed border border-gray rounded-lg">
              {companyInfo}
            </p>
          )} */}
          {!isResume ? (
            <>
              <h2 className="text-xl text-center font-semibold my-4 text-gray-700">
                Cover Letter
              </h2>
              {/* <p className="whitespace-pre-line text-gray-600 text-sm min-h-[35rem] p-3 leading-relaxed border border-gray rounded-lg">
            {generatedLetter}
          </p> */}
              {loading && !companyInfoLoading ? (
                <div className="flex flex-row space-y-2 text-gray-600 text-sm">
                  <span className="animate-spin inline w-4 h-4 border-2 mx-2 border-gray-500 border-t-transparent rounded-full"></span>
                  {processingStage}
                </div>
              ) : (
                // <div className="flex items-center justify-between border rounded-lg">
                <div
                  className={
                    generatedLetter
                      ? "border rounded-lg"
                      : "border rounded-lg bg-gray-100"
                  }

                  // className="border rounded-lg"
                >
                  <p className="whitespace-pre-line text-gray-600 text-sm min-h-[35rem] p-3 leading-relaxed">
                    {generatedLetter}
                  </p>
                  {generatedLetter && (
                    <div className="grid w-full">
                      <button
                        onClick={() => handleCopy(generatedLetter)}
                        className="p-1 justify-self-end text-center rounded-xl m-1 hover:bg-gray-100 transition"
                      >
                        {copied ? (
                          <Check className="w-5 h-5 text-gray-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {generatedLetter && !loading && (
                <>
                  <div className="relative mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3 pr-10 text-sm text-gray-700">
                    Tailored cover letters increase interviews by over 50%
                    <a
                      href="https://www.resumego.net/research/cover%20letters/"
                      target="_blank"
                      rel="noreferrer"
                      className="absolute right-3 top-3 text-blue-700 hover:text-blue-900"
                      aria-label="View tailored cover letter study"
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                  <button
                    onClick={handleCreateGoogleDoc}
                    className="btn btn-neutral w-full my-2 py-3 font-semibold rounded-lg shadow-md"
                  >
                    {!createGoogleDocLoading ? (
                      <>
                        Edit on Google Doc ($0.99) →
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          x="0px"
                          y="0px"
                          width="20"
                          height="20"
                          viewBox="0 0 48 48"
                        >
                          <path
                            fill="#2196f3"
                            d="M37,45H11c-1.657,0-3-1.343-3-3V6c0-1.657,1.343-3,3-3h19l10,10v29C40,43.657,38.657,45,37,45z"
                          ></path>
                          <path fill="#bbdefb" d="M40 13L30 13 30 3z"></path>
                          <path fill="#1565c0" d="M30 13L40 23 40 13z"></path>
                          <path
                            fill="#e3f2fd"
                            d="M15 23H33V25H15zM15 27H33V29H15zM15 31H33V33H15zM15 35H25V37H15z"
                          ></path>
                        </svg>
                      </>
                    ) : (
                      <div className="flex flex-row space-y-2 text-gray-600 text-sm">
                        <span className="animate-spin inline w-4 h-4 border-2 mx-2 border-gray-500 border-t-transparent rounded-full"></span>
                      </div>
                    )}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {resumeATS && !resumeATSLoading && (
                <>
                  <div className="relative mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3 pr-10 text-sm text-gray-700">
                    Tailored resumes increase interviews by over 100%
                    <a
                      href="https://huntr.co/research/job-search-trends-q1-2026#tailored-versus-untailored-resumes"
                      target="_blank"
                      rel="noreferrer"
                      className="absolute right-3 top-3 text-blue-700 hover:text-blue-900"
                      aria-label="View tailored resume study"
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                  <button
                    onClick={handleCreateGoogleDocResume}
                    className="btn btn-neutral w-full my-2 py-3 font-semibold rounded-lg shadow-md"
                  >
                    {!createGoogleDocResumeLoading ? (
                      <>
                        Create an ATS-tailored resume in Google Docs ($0.99) →
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        x="0px"
                        y="0px"
                        width="20"
                        height="20"
                        viewBox="0 0 48 48"
                      >
                        <path
                          fill="#2196f3"
                          d="M37,45H11c-1.657,0-3-1.343-3-3V6c0-1.657,1.343-3,3-3h19l10,10v29C40,43.657,38.657,45,37,45z"
                        ></path>
                        <path fill="#bbdefb" d="M40 13L30 13 30 3z"></path>
                        <path fill="#1565c0" d="M30 13L40 23 40 13z"></path>
                        <path
                          fill="#e3f2fd"
                          d="M15 23H33V25H15zM15 27H33V29H15zM15 31H33V33H15zM15 35H25V37H15z"
                        ></path>
                      </svg>
                      </>
                    ) : (
                      <div className="flex flex-row space-y-2 text-gray-600 text-sm">
                        <span className="animate-spin inline w-4 h-4 border-2 mx-2 border-gray-500 border-t-transparent rounded-full"></span>
                      </div>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* {generatedLetter && (
        <div className="card bg-white shadow-lg rounded-lg p-6 w-full mt-6">
          <h2 className="text-xl font-semibold mb-3 text-gray-700">
            Generated Cover Letter
          </h2>
          <p className="whitespace-pre-line text-gray-600 text-lg leading-relaxed">
            {generatedLetter}
          </p>
        </div>
      )} */}

      {/* {generatedLetter && (
        <button
          onClick={handleDownloadPDF}
          className="btn btn-success w-full py-3 text-lg font-semibold rounded-lg shadow-md mt-4"
        >
          Download as PDF
        </button>
      )} */}
    </div>
  );
}

export default App;
